import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import LoadingModal from './components/modals/LoadingModal';
import Pagination10 from './components/Pagination10';
import useDefaultModal from './components/hooks/useDefaultModal';
import useConfirmModal from './components/hooks/useConfirmModal';
import ConfirmModal from './components/modals/ConfirmModal';
import DefaultModal from './components/modals/DefaultModal';
import usePagination10 from './components/hooks/usePagination10';
import UsuarioModal from './components/modalServices/UsuarioModal';
import usePermissions from './components/hooks/usePermissions';
import PageCard from './components/PageCard';

function Usuarios() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('usuarios');
  
  const [Usuarios, setUsuarios] = useState([]);
  const [atualizar, setAtualizar] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState('');
  const [showUsuarioModal, setShowUsuarioModal] = useState(false);
  const [usuarioParaEditar, setUsuarioParaEditar] = useState(null);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState(null);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const normalizeString = str => {
    if (!str) return '';
    return str
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toUpperCase();
  };

  const usuariosFiltrados = Usuarios.filter(usu => {
    const normalizedBusca = normalizeString(filtro);
    return (
      normalizeString(usu.nome).includes(normalizedBusca) ||
      normalizeString(usu.cpf).includes(normalizedBusca) ||
      normalizeString(usu.login).includes(normalizedBusca)
    );
  });

  const totalItems = usuariosFiltrados.length;
  const { setPagination } = usePagination10(totalItems);

  const selectedUsuarios = usuariosFiltrados.slice(
    setPagination.startIndex,
    setPagination.startIndex + setPagination.rowsPerPage
  );

  const { setDefaultModal, showDefaultModal } = useDefaultModal();
  const { setConfirmModal, showConfirmModal } = useConfirmModal();

  useEffect(() => {
    console.log('TOKEN:', token);
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    setLoading(true);

    axios
      .get(`${API_BASE_URL.USUARIO}/all`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        setUsuarios(result.data);
      })
      .catch(error => {
        console.error('Erro ao obter usuários:', error);
        if (error.response?.status === 401) {
          showDefaultModal('error', 'Sessão expirada. Faça login novamente.');
          navigate('/');
        } else if (error.response?.status === 403) {
          showDefaultModal('error', 'Sem permissão para acessar os usuários.');
          navigate('/');
        } else {
          showDefaultModal('error', 'Erro ao carregar usuários.');
        }
      })
      .finally(() => {
        // Isto sempre será executado, seja em sucesso ou erro
        setLoading(false);
      });
  }, [atualizar, podeAbrir, carregandoPermissoes]);

  function handleDelete(cpf) {
    showConfirmModal('delete', () => handleConfirmDelete(cpf));
  }

  function handleConfirmDelete(cpf) {
    const loggedUser = JSON.parse(localStorage.getItem('user'));
    const loggedUserCpf = loggedUser?.cpf;

    if (cpf === loggedUserCpf) {
      showDefaultModal('error', 'Você não pode excluir a si mesmo.');
      return;
    }

    axios
      .get(`${API_BASE_URL.USUARIO_BIND}/exists_cpf/` + cpf, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then(result => {
        if (result.data.exists) {
          showDefaultModal(
            'error',
            'Este usuário possui vínculos e não pode ser excluído. Exclua os vínculos antes de tentar novamente.'
          );
        } else {
          axios
            .delete(`${API_BASE_URL.USUARIO}/delete/` + cpf, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            })
            .then(result => {
              setAtualizar(result);
              showDefaultModal(
                'success',
                'O registro foi excluído com sucesso!'
              );
            })
            .catch(error => {
              console.error('Erro ao excluir usuário:', error);
              showDefaultModal('error', 'Erro ao excluir o registro!');
            });
        }
      })
      .catch(error => {
        console.error('Erro ao verificar vínculos:', error);
        setTimeout(() => navigate('/'), 0);
      });
  }

  // Handlers para o modal de usuário
  const handleUsuarioModalClose = () => {
    setShowUsuarioModal(false);
    setUsuarioParaEditar(null);
  };

  const handleUsuarioSaveSuccess = () => {
    setAtualizar(prev => !prev); // Força recarregar a lista
    handleUsuarioModalClose();
  };

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
    <PageCard
      title="Usuários"
      icon="bi-person-badge"
      scrollable={false}
      footer={!podeAbrir ? null : (totalItems > 0 ? <Pagination10 {...setPagination} /> : null)}
    >
      {!podeAbrir ? (
        <div className="alert alert-warning m-3">
          <i className="bi bi-exclamation-triangle me-2"></i>
          <strong>Acesso Negado</strong>
          <p className="mb-0 mt-2">
            Você não tem permissão para acessar o módulo de Usuários.
          </p>
          <p className="mb-0 mt-2">
            <small>Entre em contato com o administrador do sistema.</small>
          </p>
          <button 
            className="btn btn-sm btn-outline-secondary mt-3"
            onClick={() => navigate('/home')}
          >
            <i className="bi bi-arrow-left me-1"></i>
            Voltar
          </button>
        </div>
      ) : (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <button
            className="btn btn-success btn-sm d-inline-flex align-items-center"
            onClick={() => {
              setUsuarioParaEditar(null);
              setShowUsuarioModal(true);
            }}
            disabled={!podeCriar}
            title={!podeCriar ? 'Você não tem permissão para criar usuários' : 'Novo usuário'}
          >
            <i className="bi bi-plus-lg me-1" />
            Novo
          </button>
          <button
            className="btn btn-primary btn-sm d-inline-flex align-items-center"
            onClick={() => {
              if (usuarioSelecionado) {
                setUsuarioParaEditar(usuarioSelecionado);
                setShowUsuarioModal(true);
              }
            }}
            disabled={!podeEditar || !usuarioSelecionado}
            title={!podeEditar ? 'Você não tem permissão para editar' : !usuarioSelecionado ? 'Selecione um usuário' : 'Editar usuário selecionado'}
          >
            <i className="bi bi-pencil me-1" />
            Editar
          </button>
          <button
            className="btn btn-info btn-sm d-inline-flex align-items-center"
            onClick={() => {
              if (usuarioSelecionado) {
                navigate('/usuario_vinculos', { state: { usuario: usuarioSelecionado } });
              }
            }}
            disabled={!podeEditar || !usuarioSelecionado}
            title={!podeEditar ? 'Você não tem permissão para vincular' : !usuarioSelecionado ? 'Selecione um usuário' : 'Vincular usuário selecionado'}
          >
            <i className="bi bi-link-45deg me-1" />
            Vincular
          </button>
          <div className="vr mx-1" />
          <button
            className="btn btn-danger btn-sm d-inline-flex align-items-center"
            onClick={() => {
              if (usuarioSelecionado) {
                handleDelete(usuarioSelecionado.cpf);
              }
            }}
            disabled={!podeExcluir || !usuarioSelecionado}
            title={!podeExcluir ? 'Você não tem permissão para excluir' : !usuarioSelecionado ? 'Selecione um usuário' : 'Excluir usuário selecionado'}
          >
            <i className="bi bi-trash me-1" />
            Excluir
          </button>

          <div className="ms-auto d-flex align-items-center">
            <div className="input-group" style={{ maxWidth: 300, height: 31 }}>
              <span className="input-group-text bg-white border-end-0 px-2" style={{ height: 31 }}>
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{ textTransform: 'uppercase', height: 31, fontSize: 13 }}
                placeholder="Pesquisar por nome ou CPF..."
                value={filtro}
                onChange={e => setFiltro(e.target.value.toUpperCase())}
              />
              {filtro && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltro('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
              <table className="table table-hover table-striped table-sm">
                <thead className="table-secondary sticky-top bg-light">
                  <tr>
                    <th className="px-2 align-middle" style={{ width: '15%' }}>
                      CPF
                    </th>
                    <th className="px-2 align-middle">Nome</th>
                    <th className="px-2 align-middle" style={{ width: '20%' }}>
                      Login
                    </th>
                    <th className="px-2 align-middle" style={{ width: '15%' }}>
                      Perfil
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {!isLoading &&
                    selectedUsuarios.map(usu => (
                      <tr
                        key={usu.cpf}
                        className={`${
                          usuarioSelecionado?.cpf === usu.cpf
                            ? 'table-primary'
                            : ''
                        }`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                          if (usuarioSelecionado?.cpf === usu.cpf) {
                            setUsuarioSelecionado(null); // Deselecionar se já estiver selecionado
                          } else {
                            setUsuarioSelecionado(usu); // Selecionar novo usuário
                          }
                        }}
                      >
                        <td className="px-2">{usu.cpf}</td>
                        <td className="px-2">{usu.nome}</td>
                        <td className="px-2">{usu.login}</td>
                        <td className="px-2">{usu.role}</td>
                      </tr>
                    ))}
                </tbody>
              </table>

              {/* Mensagem quando não há usuários */}
              <div className="text-center my-4">
                {!isLoading && usuariosFiltrados.length === 0 && (
                  <div className="text-muted small">
                    <i className="bi bi-info-circle me-1"></i>
                    Nenhum usuário encontrado.
                  </div>
                )}
              </div>
            </div>
        </div>
      )}

      <ConfirmModal {...setConfirmModal} />
      <DefaultModal {...setDefaultModal} />
      <UsuarioModal
        show={showUsuarioModal}
        onClose={handleUsuarioModalClose}
        onSaveSuccess={handleUsuarioSaveSuccess}
        usuario={usuarioParaEditar}
      />
    </PageCard>
  );
}

export default Usuarios;
